import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, HERO, img } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const FALLBACK = HERO;
const cover = (u) => img(u) || FALLBACK;
const date = (d) => new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

const Inline = ({ text }) => text.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p));

export const PostBody = ({ content }) => {
  const blocks = content.split(/\n\s*\n/);
  return (
    <div className="space-y-5 text-slate-700 leading-relaxed text-[17px]" data-testid="blog-post-content">
      {blocks.map((b, i) => {
        const lines = b.split("\n").filter((l) => l.trim());
        if (!lines.length) return null;
        const out = [];
        let list = [];
        const flush = () => { if (list.length) { out.push(<ul key={`u${out.length}`} className="space-y-2">{list.map((l) => <li key={l} className="flex gap-2"><span className="text-[#DC2626] font-bold">•</span><span><Inline text={l} /></span></li>)}</ul>); list = []; } };
        lines.forEach((l) => {
          if (l.startsWith("## ")) { flush(); out.push(<h2 key={`h${out.length}`} className="font-display font-extrabold text-3xl text-[#0B1D3A] pt-4">{l.slice(3)}</h2>); }
          else if (/^\s*[-•]\s+/.test(l)) list.push(l.replace(/^\s*[-•]\s+/, ""));
          else { flush(); out.push(<p key={`p${out.length}`}><Inline text={l} /></p>); }
        });
        flush();
        return <div key={i} className="space-y-4">{out}</div>;
      })}
    </div>
  );
};

export const PostCard = ({ p, big }) => (
  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className={`group flex flex-col bg-white border border-slate-200 rounded overflow-hidden hover:border-[#93C5FD] transition-colors ${big ? "md:col-span-2 md:flex-row" : ""}`}>
    <div className={`overflow-hidden ${big ? "md:w-3/5 h-64 md:h-auto" : "h-56"}`}>
      <img src={cover(p.cover)} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <span className="text-[11px] font-bold tracking-wider uppercase text-[#DC2626]">{p.category} · {date(p.created_at)}</span>
      <h2 className={`font-display font-black text-[#0B1D3A] mt-2 leading-tight group-hover:text-[#DC2626] transition-colors ${big ? "text-4xl" : "text-2xl"}`}>{p.title}</h2>
      <p className="text-sm text-slate-600 mt-3 line-clamp-3">{p.excerpt}</p>
      <span className="mt-auto pt-5 inline-flex items-center gap-1.5 font-display font-bold text-[#2563EB] group-hover:text-[#DC2626]">Leer artículo <ArrowRight size={16} /></span>
    </div>
  </Link>
);

export function BlogList() {
  const [posts, setPosts] = useState(null);
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setCats([...new Set(r.data.map((p) => p.category))])); }, []);
  useEffect(() => { setPosts(null); api.get("/blog", { params: { category: cat || undefined } }).then((r) => setPosts(r.data)); }, [cat]);
  return (
    <div data-testid="blog-page">
      <section className="bg-[#0B1D3A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <p className="text-[#60A5FA] font-bold text-sm tracking-[0.2em] uppercase">Blog FAUNNY</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl mt-2">Cuidados equinos</h1>
          <p className="text-blue-100 mt-4 max-w-2xl">Consejos de nutrición, salud e higiene para que tu caballo dé lo mejor de sí.</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["", ...cats].map((c) => (
            <button key={c || "all"} onClick={() => setParams(c ? { cat: c } : {})} data-testid={`blog-cat-${c || "all"}`}
              className={`text-sm px-4 py-2 rounded-full border font-semibold whitespace-nowrap transition-colors ${cat === c ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 hover:border-[#2563EB]"}`}>{c || "Todos"}</button>
          ))}
        </div>
        {posts === null ? <div className="h-96 bg-slate-100 animate-pulse rounded mt-8" /> : posts.length === 0 ? <p className="mt-12 text-slate-500" data-testid="blog-empty">Aún no hay artículos.</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {posts.map((p, i) => <PostCard key={p.slug} p={p} big={i === 0 && !cat && posts.length !== 3} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setP(null); setErr(false); api.get(`/blog/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <div className="py-32 text-center"><h1 className="font-display text-4xl font-black text-[#0B1D3A]">Artículo no encontrado</h1><Link to="/blog" className="text-[#DC2626] font-bold mt-4 inline-block">Volver al blog</Link></div>;
  if (!p) return <div className="max-w-3xl mx-auto px-4 py-16"><div className="h-96 bg-slate-100 animate-pulse rounded" /></div>;
  return (
    <article data-testid="blog-post-page">
      <div className="relative h-[46vh] min-h-[320px] bg-[#0B1D3A]">
        <img src={cover(p.cover)} alt={p.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D3A] to-transparent" />
        <div className="relative max-w-3xl mx-auto px-4 h-full flex flex-col justify-end pb-10 text-white">
          <span className="text-xs font-bold tracking-wider uppercase bg-[#DC2626] px-2.5 py-1 rounded-sm w-fit">{p.category}</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]" data-testid="blog-post-title">{p.title}</h1>
          <p className="text-blue-100 text-sm mt-3">{date(p.created_at)}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#2563EB] hover:text-[#DC2626]" data-testid="blog-back-link"><ArrowLeft size={15} /> Todos los artículos</Link>
        <p className="text-xl text-[#0B1D3A] font-semibold mt-6 leading-relaxed">{p.excerpt}</p>
        <div className="stripe-bar h-1 w-24 my-8" />
        <PostBody content={p.content} />
        <p className="text-xs text-slate-500 mt-10">Información general. Ante cualquier síntoma, consulta con tu veterinario.</p>
      </div>
      {p.related_products?.length > 0 && (
        <section className="bg-[#EFF6FF] py-14" data-testid="blog-related-products">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B1D3A]">Productos FAUNNY relacionados</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">{p.related_products.map((x) => <ProductCard key={x.slug} p={x} />)}</div>
          </div>
        </section>
      )}
    </article>
  );
}
